import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import stores from '@/constants/stores';
import { createAuthenticationStore } from '@/modules/authentication/engine/authentication.store';
import { createSignupStore } from '@/modules/authentication/engine/signup.store';
import { createConfirmDialogStore } from '@/modules/confirmation/engines/confirmation.store';
import { createToastStore } from '@/modules/notification/engine/notification.store';
import { createPeopleStore } from '@/modules/people/engine/people.store';
import type {
	AuthenticationState,
	ConfirmDialogState,
	HomeState,
	PeopleState,
	SidebarState,
	SignupState,
	StateStore,
	ToastState,
} from '@/types';

import { createHomeStore } from './home.store';
import { createSidebarStore } from './sidebar.store';

export const generateStoreProps = <T extends StateStore>(name: string) => ({
	name,
	onRehydrateStorage: () => (state?: T, error?: unknown) => {
		if (error || !state?.setIsHydrated) return;
		state.setIsHydrated(true);
	},
});

export const useHomeStore = create<HomeState>()(
	devtools(
		(set) => ({
			...createHomeStore(set),
		}),
		generateStoreProps(stores.INDEX),
	),
);

export const useConfirmDialogStore = create<ConfirmDialogState>()(
	devtools(
		(set) => ({
			...createConfirmDialogStore(set),
		}),
		generateStoreProps(stores.CONFIRM_DIALOG),
	),
);

export const usePeopleStore = create<PeopleState>()(
	devtools(
		(set) => ({
			...createPeopleStore(set),
		}),
		generateStoreProps(stores.PEOPLE),
	),
);

export const useToastStore = create<ToastState>()(
	devtools(
		(set) => ({
			...createToastStore(set),
		}),
		generateStoreProps(stores.TOAST),
	),
);

export const useSidebarStore = create<SidebarState>()(
	devtools(
		persist(
			(set) => ({
				...createSidebarStore(set),
			}),
			generateStoreProps(stores.SIDEBAR),
		),
	),
);

export const useAuthenticationStore = create<AuthenticationState>()(
	devtools(
		persist(
			(set, get) => ({
				...createAuthenticationStore(set, get),
			}),
			generateStoreProps(stores.AUTHENTICATION),
		),
	),
);

export const useSignupStore = create<SignupState>()(
	devtools(
		(set) => ({
			...createSignupStore(set),
		}),
		generateStoreProps(stores.SIGNUP),
	),
);
